<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: HomePage                                              |
| Section: views                                                   |
| Purpose: Landing route featuring highlights, stats, and stories. |
+------------------------------------------------------------------+
-->
<template>
    <!-- Start spotlight effect on mouse move -->
    <div ref="hero" class="hero">
        <div v-if="heroVideoAllowed" class="hero_media" aria-hidden="true">
            <video ref="heroVideo" class="hero_video" :class="{ 'hero_video--visible': heroVideoVisible }" autoplay
                muted playsinline preload="metadata" tabindex="-1" @playing="handleHeroVideoPlaying"
                @timeupdate="handleHeroVideoTimeUpdate" @ended="handleHeroVideoEnded"
                @error="handleHeroVideoError">
                <source :src="heroVideoSrc" type="video/mp4">
            </video>
        </div>
        <transition name="hero-video-control">
            <div v-if="heroVideoAllowed && heroVideoControlReady" class="hero_video-controls">
                <transition-group v-if="heroVideoPeople.length" appear name="hero-video-identification" tag="div"
                    class="hero_video-identification" role="group"
                    :aria-label="$t('home.heroVideo.featuredPeople')">
                    <span v-if="!heroVideoReplayAvailable" key="featured-people-label"
                        class="hero_video-identification-label" style="--hero-video-identification-index: 0">
                        {{ $t('home.heroVideo.featuredPeople') }}
                    </span>
                    <template v-for="(person, index) in heroVideoPeople" :key="person.personId || person.name">
                        <router-link v-if="!heroVideoReplayAvailable && person.personId"
                            :key="`person-${person.personId}`" class="hero_video-identification-person"
                            :style="{ '--hero-video-identification-index': index + 1 }"
                            :to="localePath(`/person/${person.personId}`)">
                            {{ person.name }}
                        </router-link>
                        <span v-else-if="!heroVideoReplayAvailable" :key="`person-${person.name}`"
                            class="hero_video-identification-person"
                            :style="{ '--hero-video-identification-index': index + 1 }">
                            {{ person.name }}
                        </span>
                    </template>
                </transition-group>
                <button class="hero_video-replay" type="button" :style="heroVideoProgressStyle"
                    :aria-label="heroVideoControlLabel" :title="heroVideoControlLabel" @click="handleHeroVideoControl">
                    <svg class="hero_video-progress" viewBox="0 0 44 44" aria-hidden="true" focusable="false">
                        <circle class="hero_video-progress-track" cx="22" cy="22" r="21" pathLength="1" />
                        <circle class="hero_video-progress-value" cx="22" cy="22" r="21" pathLength="1" />
                    </svg>
                    <i :class="heroVideoControlIcon" aria-hidden="true"></i>
                </button>
            </div>
        </transition>
        <!-- End spotlight effect on mouse over -->
        <NavBar :isHome="true" />
        <div class="hero_wrapper wrapper" :class="{ 'hero_wrapper--video-playing': heroVideoVisible }">
            <!-- <h1>Sejarah Thumann</h1> -->
            <img class="headline" src="@/assets/sejarah_thumann_outline.svg" alt="Sejarah Thümann - Family History">
            <!-- <div class="subtitle"> -->
            <!-- <hr> -->
            <!-- <p>Geschiedenis van de familie Thümann</p> -->
            <!-- <hr> -->
            <!-- </div> -->
            <div class="subtitle" @pointerenter="setSubtitleMorphTarget('javanese')"
                @pointerleave="setSubtitleMorphTarget('latin')">
                <p>
                    <span v-for="(char, i) in displayedSubtitle" :key="i">{{ char }}</span>
                </p>
            </div>

        </div>
        <div class="StatBar_container hide-on-mobile">
            <StatBar v-if="homeAnimationGateReady" :animateOnMount="shouldRunHomeAnimations" />
        </div>
    </div>
    <div class="StatBar_container show-on-mobile">
        <StatBar v-if="homeAnimationGateReady" :animateOnMount="shouldRunHomeAnimations" />
    </div>
    <div class="StoryCard_wrapper wrapper">
        <StoryCard v-for="(post, index) in featuredPosts" :key="post.id"
            :titleGroupTitle="post.title" :description="post.summary" :imageSrc="post.image || defaultImageSrc"
            :buttonLabel="$t('home.readMore')" :buttonHref="localePath(`/verhalen/${post.slug}`)"
            :reverse="index % 2 === 1" :transparentPhotoBackground="true" />
        <div class="TitleBtnGroup">
            <h3>{{ $t('home.discoverMoreFamilyHistory') }}</h3>
            <BtnPrimary :label="$t('home.discoverAllStories', { count: totalStories })"
                :href="localePath('/verhalen')" />
        </div>
    </div>
    <div class="PersonCardGroup_container">
        <div class="PersonCardGroup_wrapper wrapper">
            <TitleGroup :label="$t('home.featuredPeople')"
                :title="$t('home.backInTimeTitle', { link: localePath('/kalender'), date: formattedDateLocalized })"
                :isWhiteText="true" />
            <section v-if="timelineByCentury.length" class="timeline">
                <div v-for="century in timelineByCentury" :key="century.centuryStart" class="century-group">
                    <div class="century-rail">
                        <button type="button" class="century-header"
                            :aria-expanded="(expandedCenturies[century.centuryStart] !== false).toString()"
                            @click="toggleCentury(century.centuryStart)">
                            <span>{{ century.centuryLabel }}</span>
                            <span class="century-chevron" aria-hidden="true">{{
                                expandedCenturies[century.centuryStart] === false ? '›' : '▾'
                            }}</span>
                        </button>
                        <div v-show="expandedCenturies[century.centuryStart] !== false" class="century-line"></div>
                    </div>
                    <div v-show="expandedCenturies[century.centuryStart] !== false" class="timeline-entries">
                        <div v-for="(person, index) in century.entries"
                            :key="`${person.nodeID || person.personID || 'event'}-${person.eventType}-${index}`"
                            class="timeline-entry">
                            <PersonCard :photoSrc="person.photoSrc" :name="calendarPersonName(person)"
                                :years="calendarEventDescription(person)" :gender="calendarPersonGender(person)"
                                :location="calendarPersonLocation(person)" :dateIcon="true"
                                :href="calendarPersonHref(person)" :personID="person.personID"
                                :firstname="person.firstname" :lastname="person.lastname" :living="person.living"
                                :placeholder="isCalendarEventPlaceholder(person)" :isSmall="true"
                                :class="{ 'PersonCard_small_guest': isPrivatePerson(person) }" />
                        </div>
                    </div>
                </div>
            </section>
            <p v-else class="no-events-msg">—</p>
            <div class="TitleBtnGroup calendar-cta">
                <h3>{{ $t('home.calendarCtaTitle') }}</h3>
                <BtnPrimary :label="$t('home.calendarCtaButton')" :href="localePath('/kalender')"
                    iconFillColor="var(--brand-text-dark)" />
            </div>
            <img class="decoration decoration_top_right--offset" src="@/assets/decorations/pulau.svg" alt="">
        </div>
        <img class="decoration decoration_top_left--offset" src="@/assets/decorations/voc-schip.svg" alt="">
    </div>


    <!-- <div class="PersonCardGroup_container">
        <div class="PersonCardGroup_wrapper wrapper">
            <TitleGroup label="Uitgelichte personen" title="Ontdek wat er gebeurde op deze dag in de tijd"
                :isWhiteText="true" />
            <CalendarWidget />
            <img class="decoration decoration_top_right--offset" src="@/assets/decorations/pulau.svg">
        </div>
        <img class="decoration decoration_top_left--offset" src="@/assets/decorations/voc-schip.svg">
    </div> -->


    <div class="AncestorMap_container">
        <div class="AncestorMap_wrapper wrapper">
            <TitleGroup :label="$t('home.ancestorRootsLabel')" :title="$t('home.ancestorRootsTitle')" />
            <PlaceMap :hideFilters="true" :hideSearch="true" initialDisplayMode="surnames"
                :initialCenter="[118, -2]" :initialZoom="4" :forceGlobeView="true" :immediateRotation="true"
                :cooperativeGestures="true" />
            <div class="TitleBtnGroup">
                <h3>{{ $t('home.curiousPlaces') }}</h3>
                <BtnPrimary :label="$t('home.viewAllLocations')" :href="localePath('/plaatsen')" />
            </div>
            <img class="decoration decoration_bot_right" src="@/assets/decorations/bali-brown.svg" alt="">
        </div>
    </div>
    <!-- <div class="SurnameTree_container">
        <div class="SurnameTree_wrapper wrapper">
            <TitleGroup label="Uitgelichte personen" title="3 Generaties Thümann in Nederlands-Indië"
                :isWhiteText="true" />
            <SurnameTree />
        </div>
    </div> -->
    <PhotoFeature ref="photoFeature" />
    <div class="DadFamilyTree_container">
        <div class="DadFamilyTree_wrapper wrapper">
            <div class="DadFamilyTree_title_group">
                <p class="eyebrow">{{ $t('home.dadFamilyTree.label') }}</p>
                <h2 class="DadFamilyTree_title">
                    <span v-if="dadFamilyTreeTitlePrefix" class="DadFamilyTree_title_prefix">{{
                        dadFamilyTreeTitlePrefix }}</span>
                    <span class="DadFamilyTree_person_picker" @click.stop>
                        <button type="button" class="DadFamilyTree_person_trigger"
                            :aria-expanded="dadTreeDropdownOpen.toString()" aria-haspopup="listbox"
                            :aria-label="$t('home.dadFamilyTree.selectPersonAria', { name: selectedDadTreePerson.name })"
                            @click.stop="toggleDadTreeDropdown">
                            <span>{{ selectedDadTreePerson.name }}</span>
                            <i class="fas fa-caret-down DadFamilyTree_person_caret" aria-hidden="true"></i>
                        </button>
                        <ul v-if="dadTreeDropdownOpen" class="DadFamilyTree_person_menu" role="listbox">
                            <li v-for="person in dadTreePresetPeople" :key="person.id" role="option" tabindex="0"
                                :aria-selected="(person.id === selectedDadTreePersonId).toString()"
                                :class="{ 'DadFamilyTree_person_menu_item--active': person.id === selectedDadTreePersonId }"
                                @click="selectDadTreePerson(person)"
                                @keydown.enter.prevent="selectDadTreePerson(person)"
                                @keydown.space.prevent="selectDadTreePerson(person)"
                                :aria-label="$t('home.dadFamilyTree.selectPersonAria', { name: person.name })">
                                <span>{{ person.name }}</span>
                                <span v-if="isPrivatePerson(person)"></span>
                                <span v-else>{{ formatDadTreePersonYears(person) }}</span>
                            </li>
                        </ul>
                    </span><span v-if="dadFamilyTreeTitleSuffix">{{ dadFamilyTreeTitleSuffix }}</span>
                </h2>
            </div>
            <ParentTreePanel :personId="selectedDadTreePersonId" :initialGenerations="dadTreeInitialGenerations"
                :autoHeight="true" :autoHeightVerticalPadding="144" :cooperativeGestures="true" />
        </div>
    </div>
    <ContactCta />
</template>

<script>
import BtnPrimary from '@/atoms/BtnPrimary.vue';
import NavBar from '@/components/NavBar.vue';
import StatBar from '@/components/StatBar.vue';
// import CalendarWidget from '@/components/CalendarWidget.vue';
import StoryCard from '@/components/StoryCard.vue';
import PersonCard from '@/atoms/PersonCard.vue';
import TitleGroup from '@/atoms/TitleGroup.vue';
import PlaceMap from '@/components/PlaceMap.vue';
import PhotoFeature from '@/components/PhotoFeature.vue';
import ParentTreePanel from '@/components/Person/ParentTreePanel.vue';
import ContactCta from '@/components/ContactCta.vue';
// getPosts via static JSON (locale-aware)
import { getPersonById } from '@/api/personService';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { applyGenericSeo } from '@/utils/seoUtils.js';
import axios from 'axios';

import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { fetchTodayEvents } from '@/api/apiService';
import { fetchRuntimeJson } from '@/utils/runtimeJson';
import { debugLog, debugWarn } from '@/utils/logger';
import { hasLivingPersonAccess, isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';
import { normalizeGrowthRows } from '@/utils/siteStatsGrowth';
import { formatYears } from '@/utils/dateUtils';
import { createHeroVideoPlaybackController, getHeroVideoPlaybackMode, HERO_VIDEO_OUTRO_RAMP_SECONDS } from '@/utils/heroVideoPlayback.mjs';
import fallbackHeroVideoManifest from '@/data/heroVideoManifest.json';

const HOME_ANIMATION_SEEN_DATE_KEY = 'sejarahHomeAnimationsSeenDate';
const DEFAULT_HERO_VIDEO_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8090'
    : '/media/hero';
const HERO_VIDEO_BASE_URL = (process.env.VUE_APP_HERO_VIDEO_BASE_URL || DEFAULT_HERO_VIDEO_BASE_URL).replace(/\/$/, '');
const HERO_VIDEO_MANIFEST_URL = `${HERO_VIDEO_BASE_URL}/manifest.json`;
const HERO_VIDEO_FILENAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*\.mp4$/i;
const HERO_VIDEO_PERSON_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;

function normalizeHeroVideoManifest(manifest) {
    if (!Array.isArray(manifest?.videos)) return [];

    return manifest.videos
        .filter(video => typeof video?.file === 'string' && HERO_VIDEO_FILENAME_PATTERN.test(video.file))
        .map(video => ({
            file: video.file,
            src: `${HERO_VIDEO_BASE_URL}/${video.file}`,
            people: Array.isArray(video.people)
                ? video.people
                    .filter(person => typeof person?.name === 'string' && person.name.trim())
                    .map(person => ({
                        name: person.name.trim(),
                        personId: typeof person.personId === 'string'
                            && HERO_VIDEO_PERSON_ID_PATTERN.test(person.personId)
                            ? person.personId
                            : null,
                    }))
                : [],
        }));
}

function getRandomHeroVideoEntry(entries, currentFile = '') {
    const availableEntries = entries.filter(entry => entry.file !== currentFile);
    const candidates = availableEntries.length ? availableEntries : entries;
    return candidates[Math.floor(Math.random() * candidates.length)] || null;
}

export default {
    name: 'HomePage',
    components: {
        NavBar,
        StatBar,
        StoryCard,
        BtnPrimary,
        PersonCard,
        TitleGroup,
        PlaceMap,
        PhotoFeature,
        ParentTreePanel,
        ContactCta,
        // CalendarWidget
    },
    data() {
        const today = new Date();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const formattedDate = `${month}-${day}`;

        return {
            featuredStorySlugs: ['onze-molukse-wortels-in-kairatu', 'angeline-amaly-jacobsz', 'familiewapen-thumann'], // Slugs are locale-independent
            featuredPosts: [], // This will store the manually selected stories
            featuredPersonIds: ["I11", "I9", "I1"], // List of person IDs to fetch
            featuredPersons: [], // This will store the fetched person data
            totalStories: 0, // Initialize totalStories
            isInView: false, // Track if the container is in the viewport
            isPhotoFeatureInView: false,
            subtitleFinal: '',
            displayedSubtitle: [],
            randomChars: (
                "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:',.<>?/`~".split("")
            ).concat([
                // Aksara Nglegena (basisletters)
                "ꦲ", "ꦤ", "ꦕ", "ꦢ", "ꦠ", "ꦱ", "ꦗ", "ꦮ", "ꦭ", "ꦒ", "ꦚ",
                "ꦛ", "ꦩ", "ꦧ", "ꦝ", "ꦥ", "ꦪ",

                // Aksara Murda (hoofdletters / eervolle vormen)
                // "ꦑ", "ꦓ", "ꦟ", "ꦡ", "ꦦ", "ꦨ", "ꦘ", "ꦣ",

                // Aksara Swara (zelfstandige klinkers aan het begin)
                // "ꦄ", "ꦅ", "ꦆ", "ꦇ", "ꦈ", "ꦉ", "ꦊ", "ꦋ", "ꦌ", "ꦍ", "ꦎ",

                // Hiasan & tanda baca Jawa
                // "꧁", "꧂", // hiasan begin/einde
                // "꧃", "꧄", "꧅", "꧆", "꧇", "꧈", "꧉", "꧊", "꧋", "꧌", "꧍", "ꧏ",

                // Angka Jawa (Javanese cijfers)
                // "꧐", "꧑", "꧒", "꧓", "꧔", "꧕", "꧖", "꧗", "꧘", "꧙"
            ]),
            subtitleHanacaraka: "ꦱꦗꦫꦃꦏꦸꦭꦮꦂꦒꦛꦸꦩꦤ꧀",
            subtitleMorphProgress: 0,
            subtitleMorphTarget: 'javanese',
            subtitleMorphFrame: null,
            subtitleMorphHoldTimeout: null,
            subtitleMorphLastTimestamp: null,
            subtitleMorphStarted: false,
            subtitleAutoInitialized: false,
            homePageMounted: false,
            homeAnimationGateReady: false,
            shouldRunHomeAnimations: true,
            heroVideoSrc: '',
            heroVideoEntries: [],
            heroVideoEntry: null,
            heroVideoAllowed: false,
            heroVideoVisible: false,
            heroVideoReplayAvailable: false,
            heroVideoControlReady: false,
            heroVideoPaused: false,
            heroVideoProgress: 0,
            heroVideoProgressFrame: null,
            heroVideoRevealTimeout: null,
            heroVideoMotionQuery: null,
            heroVideoPlaybackMode: 'fixed',
            heroVideoPlaybackController: null,
            eventOrder: ["birth", "baptism", "death", "burial"],
            formattedDate,
            orderedEventData: {},
            expandedCenturies: {},
            enableSpotlight: false,
            spotlightDisabled: false,
            spotlightEnabled: false, // Add this variable
            selectedDadTreePersonId: 'I3',
            dadTreeDropdownOpen: false,
            dadTreePresetPeople: [
                { id: 'I3', name: 'Rudy Thümann' },
                { id: 'I43', name: 'Jeffrey Thümann' },
                { id: 'I1', name: 'Charles Jozef Thümann' },
                { id: 'I2', name: 'Angeline Amaly Jacobsz' },
                { id: 'I15', name: 'Francis Duncan Jacobsz' },
                { id: 'I16', name: 'Alexandrien Frederique Westhoff' },
            ],
        };
    },
    setup() {
        const { authToken } = useAuth();
        const { locale, localePath } = useLocale();
        return { authToken, locale, localePath };
    },
    watch: {
        '$i18n.locale'(newLocale) {
            this.loadFeaturedPosts(newLocale);
        },
    },
    async created() {

        this.subtitleFinal = this.$t('home.heroSubtitle');
        await this.prepareHomeAnimationGate();

        // Apply generic SEO for the homepage:
        const title = this.$t('home.seoTitle');
        const description = this.$t('home.seoDescription');
        const image = "https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg";
        applyGenericSeo({
            title,
            description,
            image,
            currentLocale: this.locale,
            route: '/'
        }, {
            includeTwitter: true,
            includeHreflangs: true
        });

        this.loadFeaturedPosts(this.$i18n.locale);
        this.loadDadTreePresetPeople();

        try {
            const persons = await Promise.all(this.featuredPersonIds.map(async (id) => {
                const person = await getPersonById(id);

                // Attempt to fetch the profile photo
                let photoPath;
                try {
                    const photoResponse = await axios.get(`${process.env.VUE_APP_BACKEND_API_URL}/person-default-photo/${id}`, {
                        headers: {
                            'X-API-KEY': process.env.VUE_APP_API_KEY
                        }
                    });
                    photoPath = photoResponse.data
                        ? `${process.env.VUE_APP_PHOTO_API_URL}/${encodeURIComponent(photoResponse.data.thumbpath)}`
                        : null;
                } catch (error) {
                    // Log the error if the photo fetch fails
                    debugWarn(`No custom photo for personID ${id}. Using gender-based default.`);
                    photoPath = getDefaultPhoto(person.sex); // Fall back to gender-based default
                }

                debugLog(`Final photoSrc for personID ${person.personID} (${person.sex}):`, photoPath); // Debugging

                return {
                    id: person.personID,
                    photoSrc: photoPath,
                    name: `${person.firstname} ${person.lastname}`,
                    years: person.deathdatetr === '0000-00-00'
                        ? `${new Date(person.birthdatetr).getFullYear()}–(in leven)`
                        : `${new Date(person.birthdatetr).getFullYear()}–${new Date(person.deathdatetr).getFullYear()}`,
                    gender: person.sex,  // Use 'M' or 'F' directly for consistency
                    href: `/person/${person.personID}`
                };
            }));

            this.featuredPersons = persons;
            debugLog("Final structured featuredPersons with photos:", this.featuredPersons); // Debugging
        } catch (error) {
            console.error("Error fetching person data:", error);
        }

        let eventsArray = await fetchRuntimeJson(`/data/getCalendarEvents/daily/${this.formattedDate}.json`, []);
        if (hasLivingPersonAccess(this.authToken)) {
            try {
                eventsArray = await fetchTodayEvents(this.formattedDate);
            } catch (error) {
                debugWarn('Could not refresh authenticated calendar events; using the public snapshot.', error);
            }
        }

        const groupedEvents = {};
        eventsArray.forEach(event => {
            if (!event.placeholder && (!event.photoSrc || event.photoSrc.trim() === '')) {
                event.photoSrc = getDefaultPhoto(event.sex);
            }

            if (!groupedEvents[event.eventType]) {
                groupedEvents[event.eventType] = [];
            }
            groupedEvents[event.eventType].push(event);
        });

        // Sorteer zoals in EventCalendar
        const ordered = {};
        this.eventOrder.forEach(type => {
            if (groupedEvents[type]) {
                ordered[type] = groupedEvents[type];
            }
        });

        this.orderedEventData = ordered;

    },
    mounted() {
        this.homePageMounted = true;
        this.startAutomaticSubtitleIfReady();
        this.initializeHeroVideo();
        document.addEventListener('click', this.closeDadTreeDropdown);
        document.addEventListener('keydown', this.handleDadTreeKeydown);

        // Start spotlight effect on mouse move

        // const heroEl = this.$refs.hero;
        // const TOP_OFFSET = 0;
        // const BOTTOM_OFFSET = 84;
        // const RADIUS = '144px'; // or whatever your spotlight size is

        // heroEl.addEventListener('mousemove', e => {
        //     const { left, top, height } = heroEl.getBoundingClientRect();
        //     const x = e.clientX - left;
        //     const y = e.clientY - top;

        //     // update center coordinates
        //     heroEl.style.setProperty('--x', `${x}px`);
        //     heroEl.style.setProperty('--y', `${y}px`);

        //     // only open if we're inside the “safe” band:
        //     const inBand = y > TOP_OFFSET && y < (height - BOTTOM_OFFSET);
        //     const open = inBand && heroEl.matches(':hover');

        //     heroEl.style.setProperty('--r', open ? RADIUS : '0px');
        // });

        // heroEl.addEventListener('mouseleave', () => {
        //     heroEl.style.setProperty('--r', '0px');
        // });

        if (!this.enableSpotlight) return;

        setTimeout(() => {
            this.spotlightEnabled = true;
        }, 7000);

        const heroEl = this.$refs.hero;
        const disableEls = heroEl.querySelectorAll('.StatBar_container a, .NavBar li, .search-box');

        // Disable spotlight when hovering over these elements
        disableEls.forEach(el => {
            el.addEventListener('mouseenter', () => {
                this.spotlightDisabled = true;
                heroEl.style.setProperty('--r', '0px'); // Hide the spotlight
            });

            el.addEventListener('mouseleave', () => {
                this.spotlightDisabled = false;
            });
        });

        // Run the check on mount
        this.disableSpotlight();

        // Add a resize event listener to handle screen size changes
        window.addEventListener('resize', this.disableSpotlight);

        // ② your existing mousemove, but bail out when disabled
        const TOP_OFFSET = 0;
        const BOTTOM_OFFSET = 84;
        const RADIUS = '144px';

        heroEl.addEventListener('mousemove', e => {
            if (!this.spotlightEnabled || this.spotlightDisabled) return; // Check spotlightEnabled

            const { left, top, height } = heroEl.getBoundingClientRect();
            const x = e.clientX - left;
            const y = e.clientY - top;
            heroEl.style.setProperty('--x', `${x}px`);
            heroEl.style.setProperty('--y', `${y}px`);

            const inBand = y > TOP_OFFSET && y < (height - BOTTOM_OFFSET);
            const open = inBand && heroEl.matches(':hover');
            heroEl.style.setProperty('--r', open ? RADIUS : '0px');
        });

        heroEl.addEventListener('mouseleave', () => {
            if (!this.spotlightEnabled) return; // Check spotlightEnabled
            heroEl.style.setProperty('--r', '0px');
        });

        // Now: a continuous “float” loop
        const AMP = 8;           // max drift in px
        const FREQ_X = 1.7;      // cycles/sec on X
        const FREQ_Y = 1.3;      // cycles/sec on Y
        let start = performance.now();

        function floatStep(now) {
            const t = (now - start) / 1000;
            const dx = Math.sin(t * FREQ_X) * AMP;
            const dy = Math.cos(t * FREQ_Y) * AMP;
            heroEl.style.setProperty('--dx', `${dx}px`);
            heroEl.style.setProperty('--dy', `${dy}px`);
            requestAnimationFrame(floatStep);
        }
        requestAnimationFrame(floatStep);

        // End spotlight effect on mouse move

        const photoFeatureElement = this.$refs.photoFeature.$el; // Reference to the PhotoFeature component element

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.isPhotoFeatureInView = true;
                        window.addEventListener('scroll', this.handlePhotoFeatureScroll);
                    } else {
                        this.isPhotoFeatureInView = false;
                        window.removeEventListener('scroll', this.handlePhotoFeatureScroll);
                    }
                });
            },
            {
                root: null, // Use the viewport as the root
                threshold: 0.1 // Trigger when 10% of the element is visible
            }
        );

        if (photoFeatureElement) {
            observer.observe(photoFeatureElement);
        }
    },
    beforeUnmount() {
        if (this.subtitleMorphHoldTimeout) {
            clearTimeout(this.subtitleMorphHoldTimeout);
        }
        if (this.subtitleMorphFrame) {
            cancelAnimationFrame(this.subtitleMorphFrame);
        }
        if (this.heroVideoRevealTimeout) {
            clearTimeout(this.heroVideoRevealTimeout);
        }
        this.stopHeroVideoProgress();
        if (this.heroVideoMotionQuery) {
            this.heroVideoMotionQuery.removeEventListener('change', this.syncHeroVideoPreference);
        }
        window.removeEventListener('resize', this.disableSpotlight);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('scroll', this.handlePhotoFeatureScroll);
        document.removeEventListener('click', this.closeDadTreeDropdown);
        document.removeEventListener('keydown', this.handleDadTreeKeydown);
    },
    computed: {
        defaultImageSrc() {
            return `${process.env.VUE_APP_STRAPI_API_URL.replace('/api', '')}/uploads/sejarah_thumann_f44b3c03a9.jpg`;
            // return require('@/assets/sejarah_thumann1080x781.jpg');
        },
        formattedDateLocalized() {
            return new Date().toLocaleDateString(this.localeTag(), {
                day: 'numeric',
                month: 'long'
            });
        },
        selectedDadTreePerson() {
            return this.dadTreePresetPeople.find(person => person.id === this.selectedDadTreePersonId)
                || { id: this.selectedDadTreePersonId, name: 'Rudy Thümann' };
        },
        dadFamilyTreeTitlePrefix() {
            const prefix = this.$t('home.dadFamilyTree.titlePrefix');
            return prefix === 'home.dadFamilyTree.titlePrefix' ? '' : prefix;
        },
        dadFamilyTreeTitleSuffix() {
            const suffix = this.$t('home.dadFamilyTree.titleSuffix');
            return suffix === 'home.dadFamilyTree.titleSuffix' ? '' : suffix;
        },
        dadTreeInitialGenerations() {
            return this.selectedDadTreePersonId === 'I16' ? 4 : 2;
        },
        heroVideoControlLabel() {
            if (this.heroVideoReplayAvailable) return this.$t('home.heroVideo.playAnother');
            if (this.heroVideoPaused) return this.$t('home.heroVideo.resume');
            return this.$t('home.heroVideo.pause');
        },
        heroVideoControlIcon() {
            if (this.heroVideoReplayAvailable) return 'fa-solid fa-arrow-rotate-right';
            if (this.heroVideoPaused) return 'fa-solid fa-play';
            return 'fa-solid fa-pause';
        },
        heroVideoProgressStyle() {
            return { '--hero-video-progress': this.heroVideoProgress };
        },
        heroVideoPeople() {
            return this.heroVideoEntry?.people || [];
        },
        timelineByCentury() {
            const entries = [];
            Object.entries(this.orderedEventData).forEach(([eventType, members]) => {
                members.forEach(person => entries.push({ ...person, eventType }));
            });

            entries.sort((a, b) => {
                const yearA = a.eventDate ? new Date(a.eventDate).getFullYear() : 0;
                const yearB = b.eventDate ? new Date(b.eventDate).getFullYear() : 0;
                return yearA - yearB;
            });

            const centuries = {};
            entries.forEach(entry => {
                const year = entry.eventDate ? new Date(entry.eventDate).getFullYear() : 0;
                const centuryStart = Math.floor(year / 100) * 100;
                if (!centuries[centuryStart]) centuries[centuryStart] = [];
                centuries[centuryStart].push(entry);
            });

            return Object.keys(centuries)
                .sort((a, b) => Number(a) - Number(b))
                .map(centuryStart => ({
                    centuryStart: Number(centuryStart),
                    centuryLabel: `${centuryStart}s`,
                    entries: centuries[centuryStart]
                }));
        }
    },
    methods: {
        initializeHeroVideo() {
            this.heroVideoPlaybackMode = getHeroVideoPlaybackMode(
                typeof navigator === 'undefined' ? {} : navigator
            );
            if (process.env.NODE_ENV === 'development') {
                const requestedMode = new URLSearchParams(window.location.search).get('heroPlayback');
                if (['legacy', 'bounded', 'fixed'].includes(requestedMode)) {
                    this.heroVideoPlaybackMode = requestedMode;
                }
                debugLog('Hero video playback mode:', this.heroVideoPlaybackMode);
            }
            this.heroVideoPlaybackController = createHeroVideoPlaybackController({ mode: this.heroVideoPlaybackMode });
            this.heroVideoEntries = normalizeHeroVideoManifest(fallbackHeroVideoManifest);
            this.selectRandomHeroVideo();
            this.loadHeroVideoManifest();

            if (typeof window === 'undefined' || !window.matchMedia) {
                this.heroVideoAllowed = true;
                return;
            }

            this.heroVideoMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
            this.syncHeroVideoPreference(this.heroVideoMotionQuery);
            this.heroVideoMotionQuery.addEventListener('change', this.syncHeroVideoPreference);
        },
        async loadHeroVideoManifest() {
            const manifest = await fetchRuntimeJson(HERO_VIDEO_MANIFEST_URL, fallbackHeroVideoManifest);
            const entries = normalizeHeroVideoManifest(manifest);
            if (!entries.length) {
                debugWarn('Hero video manifest contains no valid video entries; using the bundled fallback.');
                return;
            }

            const currentFile = this.heroVideoEntry?.file;
            this.heroVideoEntries = entries;

            const updatedCurrentEntry = entries.find(entry => entry.file === currentFile);
            if (updatedCurrentEntry) {
                this.heroVideoEntry = updatedCurrentEntry;
                this.heroVideoSrc = updatedCurrentEntry.src;
                return;
            }

            if (!this.heroVideoControlReady) this.selectRandomHeroVideo();
        },
        selectRandomHeroVideo(currentFile = '') {
            let entry = getRandomHeroVideoEntry(this.heroVideoEntries, currentFile);
            if (process.env.NODE_ENV === 'development' && !currentFile) {
                const requestedFile = new URLSearchParams(window.location.search).get('heroVideo');
                entry = this.heroVideoEntries.find(video => video.file === requestedFile) || entry;
            }
            if (!entry) return false;

            this.heroVideoEntry = entry;
            this.heroVideoSrc = entry.src;
            return true;
        },
        syncHeroVideoPreference(mediaQuery) {
            const prefersReducedMotion = mediaQuery.matches;
            this.heroVideoAllowed = !prefersReducedMotion;

            if (prefersReducedMotion) {
                this.heroVideoVisible = false;
                this.heroVideoReplayAvailable = false;
                this.heroVideoControlReady = false;
                this.heroVideoPaused = false;
                this.heroVideoProgress = 0;
                this.stopHeroVideoProgress();
                if (this.heroVideoRevealTimeout) {
                    clearTimeout(this.heroVideoRevealTimeout);
                    this.heroVideoRevealTimeout = null;
                }
            }
        },
        handleHeroVideoPlaying() {
            this.revealHeroVideo();
            this.startHeroVideoProgress();
        },
        revealHeroVideo() {
            if (!this.heroVideoAllowed) return;

            this.heroVideoPaused = false;
            if (this.heroVideoControlReady) return;
            if (this.heroVideoVisible) {
                this.heroVideoControlReady = true;
                return;
            }
            if (this.heroVideoRevealTimeout) return;

            this.heroVideoReplayAvailable = false;
            this.heroVideoRevealTimeout = setTimeout(() => {
                // Preserve the old seek only for the development comparison.
                if (this.heroVideoPlaybackMode === 'legacy' && this.$refs.heroVideo) {
                    this.$refs.heroVideo.currentTime = 0;
                    this.heroVideoProgress = 0;
                }
                this.heroVideoVisible = true;
                this.heroVideoControlReady = true;
                this.heroVideoRevealTimeout = null;
            }, 1200);
        },
        handleHeroVideoTimeUpdate(event) {
            this.syncHeroVideoProgress(event.currentTarget);
        },
        syncHeroVideoProgress(video) {
            if (!Number.isFinite(video.duration) || video.duration <= 0) return;

            this.heroVideoPlaybackController?.sync(video);

            this.heroVideoProgress = Math.min(1, Math.max(0, video.currentTime / video.duration));
            if (!this.heroVideoVisible) return;

            if (video.duration - video.currentTime <= HERO_VIDEO_OUTRO_RAMP_SECONDS) {
                this.hideHeroVideo();
            }
        },
        startHeroVideoProgress() {
            this.stopHeroVideoProgress();

            const updateProgress = () => {
                const video = this.$refs.heroVideo;
                if (!video || video.paused || video.ended) {
                    this.heroVideoProgressFrame = null;
                    return;
                }

                this.syncHeroVideoProgress(video);
                this.heroVideoProgressFrame = requestAnimationFrame(updateProgress);
            };

            updateProgress();
        },
        stopHeroVideoProgress() {
            if (this.heroVideoProgressFrame === null) return;

            cancelAnimationFrame(this.heroVideoProgressFrame);
            this.heroVideoProgressFrame = null;
        },
        handleHeroVideoEnded() {
            this.stopHeroVideoProgress();
            this.heroVideoProgress = 1;
            this.hideHeroVideo();
            this.heroVideoReplayAvailable = true;
            this.heroVideoControlReady = true;
            this.heroVideoPaused = false;
        },
        handleHeroVideoError() {
            this.stopHeroVideoProgress();
            this.heroVideoVisible = false;
            this.heroVideoReplayAvailable = true;
            this.heroVideoControlReady = true;
            this.heroVideoPaused = false;
            this.heroVideoProgress = 0;
            debugWarn('Hero video could not be loaded; retaining the static hero image.');
        },
        hideHeroVideo() {
            this.heroVideoVisible = false;
        },
        handleHeroVideoControl() {
            if (this.heroVideoReplayAvailable) {
                this.playAnotherHeroVideo();
                return;
            }

            if (this.heroVideoPaused) {
                this.resumeHeroVideo();
                return;
            }

            this.pauseHeroVideo();
        },
        pauseHeroVideo() {
            const video = this.$refs.heroVideo;
            if (!video || video.paused) return;

            video.pause();
            this.stopHeroVideoProgress();
            this.syncHeroVideoProgress(video);
            this.heroVideoPaused = true;
        },
        resumeHeroVideo() {
            const video = this.$refs.heroVideo;
            if (!video) return;

            const playPromise = video.play();
            if (playPromise) playPromise.catch(this.handleHeroVideoError);
        },
        playAnotherHeroVideo() {
            if (!this.heroVideoAllowed) return;

            if (this.heroVideoRevealTimeout) {
                clearTimeout(this.heroVideoRevealTimeout);
                this.heroVideoRevealTimeout = null;
            }

            const video = this.$refs.heroVideo;
            if (video) {
                video.pause();
                if (video.playbackRate !== 1) video.playbackRate = 1;
            }
            this.heroVideoPlaybackController?.reset();
            this.stopHeroVideoProgress();

            this.heroVideoVisible = false;
            this.heroVideoReplayAvailable = false;
            this.heroVideoControlReady = false;
            this.heroVideoPaused = false;
            this.heroVideoProgress = 0;
            if (!this.selectRandomHeroVideo(this.heroVideoEntry?.file)) {
                this.handleHeroVideoError();
                return;
            }

            this.$nextTick(() => {
                const nextVideo = this.$refs.heroVideo;
                if (!nextVideo) return;

                nextVideo.load();
                const playPromise = nextVideo.play();
                if (playPromise) playPromise.catch(this.handleHeroVideoError);
            });
        },
        toggleCentury(centuryStart) {
            this.expandedCenturies[centuryStart] = this.expandedCenturies[centuryStart] !== false ? false : true;
        },
        toggleDadTreeDropdown() {
            this.dadTreeDropdownOpen = !this.dadTreeDropdownOpen;
        },
        closeDadTreeDropdown() {
            this.dadTreeDropdownOpen = false;
        },
        handleDadTreeKeydown(event) {
            if (event.key === 'Escape') {
                this.closeDadTreeDropdown();
            }
        },
        selectDadTreePerson(person) {
            this.selectedDadTreePersonId = person.id;
            this.closeDadTreeDropdown();
        },
        async loadDadTreePresetPeople() {
            const enrichedPeople = await Promise.all(this.dadTreePresetPeople.map(async (presetPerson) => {
                try {
                    const person = await getPersonById(presetPerson.id);
                    return {
                        ...person,
                        ...presetPerson,
                        personID: person.personID || presetPerson.id,
                    };
                } catch (error) {
                    debugWarn(`Could not load Dad family tree picker metadata for ${presetPerson.id}.`);
                    return presetPerson;
                }
            }));

            this.dadTreePresetPeople = enrichedPeople;
        },
        formatDadTreePersonYears(person) {
            const hasYearSource = [
                person.birthdatetr,
                person.altbirthdatetr,
                person.deathdatetr,
                person.burialdatetr,
            ].some(date => date && date !== '0000-00-00');

            if (!hasYearSource && Number(person.living) !== 1) {
                return '';
            }

            return formatYears(
                person.birthdatetr,
                person.altbirthdatetr,
                person.deathdatetr,
                person.burialdatetr,
                person.living,
                this.locale
            );
        },
        async prepareHomeAnimationGate() {
            const animationKey = await this.getHomeAnimationKey();
            const seenDate = this.getStoredHomeAnimationSeenDate();

            this.shouldRunHomeAnimations = seenDate !== animationKey;
            this.homeAnimationGateReady = true;

            if (this.shouldRunHomeAnimations) {
                this.setStoredHomeAnimationSeenDate(animationKey);
            } else {
                this.renderFinalSubtitle();
            }

            this.startAutomaticSubtitleIfReady();
        },
        async getHomeAnimationKey() {
            const fallbackKey = this.getTodayKey();
            const growthData = await fetchRuntimeJson('/data/getSiteStats/siteStatsGrowth.json', null);
            const rows = normalizeGrowthRows(growthData?.rows);
            return rows[rows.length - 1]?.date || fallbackKey;
        },
        getTodayKey() {
            return new Date().toISOString().split('T')[0];
        },
        getStoredHomeAnimationSeenDate() {
            try {
                return localStorage.getItem(HOME_ANIMATION_SEEN_DATE_KEY);
            } catch (error) {
                return null;
            }
        },
        setStoredHomeAnimationSeenDate(value) {
            try {
                localStorage.setItem(HOME_ANIMATION_SEEN_DATE_KEY, value);
            } catch (error) {
                // Ignore storage failures so private browsing or disabled storage does not block the page.
            }
        },
        startAutomaticSubtitleIfReady() {
            if (!this.homePageMounted || !this.homeAnimationGateReady || this.subtitleAutoInitialized) {
                return;
            }

            this.subtitleAutoInitialized = true;

            if (this.shouldRunHomeAnimations) {
                this.animateMorphingSubtitle();
                return;
            }

            this.renderFinalSubtitle();
        },
        renderFinalSubtitle() {
            this.subtitleMorphProgress = 1;
            this.subtitleMorphTarget = 'latin';
            this.subtitleMorphStarted = false;
            this.subtitleMorphLastTimestamp = null;
            this.displayedSubtitle = this.subtitleFinal.split("");
        },
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        loadFeaturedPosts(locale) {
            try {
                let posts;
                try {
                    posts = require(`@/data/getBlogPosts/posts_${locale}.json`).data;
                } catch {
                    posts = require('@/data/getBlogPosts/posts.json').data;
                }
                this.totalStories = posts.length;
                this.featuredPosts = this.featuredStorySlugs
                    .map(slug => posts.find(p => p.slug === slug))
                    .filter(Boolean);
            } catch (error) {
                console.error('Error loading featured posts:', error);
            }
        },
        formatEventDescription(eventType, eventDate) {
            if (!eventDate) return '';
            const year = new Date(eventDate).getFullYear();
            const descriptions = {
                birth: this.$t('home.eventDescription.birth', { year }),
                baptism: this.$t('home.eventDescription.baptism', { year }),
                death: this.$t('home.eventDescription.death', { year }),
                burial: this.$t('home.eventDescription.burial', { year }),
            };
            return descriptions[eventType] || this.$t('home.eventDescription.unknown', { year });
        },
        isCalendarEventPlaceholder(person) {
            return person?.placeholder === true
                || isLivingPlaceholder(person)
                || isPrivateLivingPerson(person, this.authToken);
        },
        calendarPersonName(person) {
            if (this.isCalendarEventPlaceholder(person)) return this.$t('privacy.livingPlaceholder');
            return [person?.firstname, person?.lastname].filter(Boolean).join(' ');
        },
        calendarPersonGender(person) {
            return this.isCalendarEventPlaceholder(person) ? 'unknown' : (person?.sex || 'unknown');
        },
        calendarPersonHref(person) {
            return this.isCalendarEventPlaceholder(person) || !person?.personID
                ? 'javascript:void(0)'
                : this.localePath(`/person/${person.personID}`);
        },
        calendarEventDescription(person) {
            return this.isCalendarEventPlaceholder(person)
                ? ''
                : this.formatEventDescription(person?.eventType, person?.eventDate);
        },
        calendarPersonLocation(person) {
            return this.isCalendarEventPlaceholder(person) ? '' : (person?.eventPlace || '');
        },
        disableSpotlight() {
            const heroEl = this.$refs.hero;
            if (window.innerWidth <= 860) {
                this.spotlightDisabled = true;
                heroEl.style.setProperty('--r', '0px'); // Ensure the spotlight is hidden
            } else {
                this.spotlightDisabled = false;
            }
        },
        animateMorphingSubtitle() {
            const holdDuration = 1000; // hoe lang de Javaanse zin blijft staan

            if (this.subtitleMorphHoldTimeout) {
                clearTimeout(this.subtitleMorphHoldTimeout);
            }
            if (this.subtitleMorphFrame) {
                cancelAnimationFrame(this.subtitleMorphFrame);
            }

            this.subtitleMorphProgress = 0;
            this.subtitleMorphTarget = 'javanese';
            this.subtitleMorphFrame = null;
            this.subtitleMorphLastTimestamp = null;
            this.subtitleMorphStarted = false;
            this.renderSubtitleMorph();

            this.subtitleMorphHoldTimeout = setTimeout(() => {
                this.subtitleMorphHoldTimeout = null;
                this.setSubtitleMorphTarget('latin');
            }, holdDuration);
        },
        setSubtitleMorphTarget(target) {
            this.subtitleMorphTarget = target;

            if (target === 'javanese' && this.subtitleMorphHoldTimeout) {
                clearTimeout(this.subtitleMorphHoldTimeout);
                this.subtitleMorphHoldTimeout = null;
            }

            this.startSubtitleMorphLoop();
        },
        startSubtitleMorphLoop() {
            if (this.subtitleMorphFrame) return;

            this.subtitleMorphStarted = true;
            this.subtitleMorphLastTimestamp = null;
            this.subtitleMorphFrame = requestAnimationFrame(this.stepSubtitleMorph);
        },
        stepSubtitleMorph(timestamp) {
            if (!this.subtitleMorphLastTimestamp) {
                this.subtitleMorphLastTimestamp = timestamp;
                this.renderSubtitleMorph();
                this.subtitleMorphFrame = requestAnimationFrame(this.stepSubtitleMorph);
                return;
            }

            const elapsed = timestamp - this.subtitleMorphLastTimestamp;
            const targetProgress = this.subtitleMorphTarget === 'latin' ? 1 : 0;
            const distance = targetProgress - this.subtitleMorphProgress;

            if (Math.abs(distance) <= 0.001) {
                this.subtitleMorphProgress = targetProgress;
                this.renderSubtitleMorph();
                this.subtitleMorphFrame = null;
                this.subtitleMorphStarted = false;
                this.subtitleMorphLastTimestamp = null;
                return;
            }

            const morphSpeed = 0.001;
            const progressStep = Math.min(Math.abs(distance), elapsed * morphSpeed);

            this.subtitleMorphLastTimestamp = timestamp;
            this.subtitleMorphProgress += Math.sign(distance) * progressStep;
            this.renderSubtitleMorph();

            this.subtitleMorphFrame = requestAnimationFrame(this.stepSubtitleMorph);
        },
        getSubtitleMorphSourceChars() {
            return this.subtitleHanacaraka.split("");
        },
        getSubtitleMorphRenderProgress() {
            if (this.subtitleMorphProgress <= 0 || this.subtitleMorphProgress >= 1) {
                return this.subtitleMorphProgress;
            }

            if (this.subtitleMorphTarget === 'latin') {
                return 1 - Math.pow(1 - this.subtitleMorphProgress, 3);
            }

            return this.subtitleMorphProgress < 0.5
                ? 4 * Math.pow(this.subtitleMorphProgress, 3)
                : 1 - Math.pow(-2 * this.subtitleMorphProgress + 2, 3) / 2;
        },
        renderSubtitleMorph() {
            const finalChars = this.subtitleFinal.split("");
            const sourceChars = this.getSubtitleMorphSourceChars();
            const length = Math.max(finalChars.length, sourceChars.length);
            const transitionBand = 0.12;
            const renderProgress = this.getSubtitleMorphRenderProgress();

            if (renderProgress <= 0) {
                this.displayedSubtitle = sourceChars;
                return;
            }

            if (renderProgress >= 1) {
                this.displayedSubtitle = finalChars;
                return;
            }

            this.displayedSubtitle = Array.from({ length }, (_, index) => {
                const threshold = (index + 1) / (length + 1);
                const lowerBound = threshold - (transitionBand / 2);
                const upperBound = threshold + (transitionBand / 2);

                if (renderProgress <= lowerBound) {
                    return sourceChars[index] || '';
                }

                if (renderProgress >= upperBound) {
                    return finalChars[index] || '';
                }

                if (finalChars[index] === ' ') {
                    return ' ';
                }

                return this.randomChars[Math.floor(Math.random() * this.randomChars.length)];
            });
        },
        handlePhotoFeatureScroll() {
            if (!this.isPhotoFeatureInView) return;

            // Handle scrolling logic for PhotoFeature
            const photoFeature = this.$refs.photoFeature;
            const scrollY = window.scrollY;
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = scrollY / maxScroll;

            // Pass the scroll percent to the PhotoFeature component
            photoFeature.updateGalleryPosition(scrollPercent);
        },
        localeTag() {
            if (this.locale === 'en') return 'en-US';
            if (this.locale === 'id') return 'id-ID';
            return 'nl-NL';
        },
    }
};
</script>

<style scoped>
.hero {
    /* background: linear-gradient(0deg, rgba(84, 46, 35, 0.75) 0%, rgba(84, 46, 35, 0.50) 100%), url('@/assets/sejarah_thumann1080x781.jpg') lightgray 50% / cover no-repeat; */
    height: calc(100vh - 144px);
    min-height: 600px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    /* transition: background-position 0.2s ease; */
    /* Smooth transition */
}

/* Start spotlight effect on mouse move */

.hero {
    position: relative;
    /* Dropdowns are allowed to escape the hero; the media layer clips the video itself. */
    overflow: visible;
    background:
        linear-gradient(0deg, rgba(84, 46, 35, 0.75) 0%, rgba(84, 46, 35, 0.5) 100%),
        url('@/assets/sejarah_thumann1080x781.jpg') 50%/cover no-repeat;
}

.hero_media {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
}

.hero_media::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(0deg, rgba(84, 46, 35, 0.75) 0%, rgba(84, 46, 35, 0.5) 100%);
}

.hero_video {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 50% 50%;
    opacity: 0;
    filter: sepia(0.42) saturate(0.62) contrast(1.08) brightness(0.92);
    transition: opacity 4s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero_video--visible {
    opacity: 1;
    transition-duration: 3s;
}

.hero_video-controls {
    position: absolute;
    right: 32px;
    bottom: 168px;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    max-width: calc(100% - 64px);
}

.hero_video-identification {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 4px 6px;
    min-width: 0;
    color: var(--brand-text-light);
    text-align: right;
}

.hero_video-identification-label {
    font-size: 0.52rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.12em;
    opacity: 0.62;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
    white-space: nowrap;
}

.hero_video-identification-person {
    display: inline-flex;
    align-items: center;
    padding: 4px 7px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 999px;
    background: rgba(42, 27, 22, 0.62);
    color: inherit;
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

a.hero_video-identification-person:hover,
a.hero_video-identification-person:focus-visible {
    border-color: rgba(255, 255, 255, 0.48);
    background: rgba(42, 27, 22, 0.84);
    color: #fff;
}

.hero-video-identification-enter-active,
.hero-video-identification-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
    transition-delay: calc(var(--hero-video-identification-index, 0) * 80ms);
}

.hero-video-identification-enter-from,
.hero-video-identification-leave-to {
    opacity: 0;
    transform: translateX(4px);
}

.hero_video-replay {
    --hero-video-progress: 0;
    position: relative;
    flex: 0 0 auto;
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(42, 27, 22, 0.72);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    color: var(--brand-text-light);
    cursor: pointer;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    transition: transform 0.2s ease, background-color 0.2s ease;
}

.hero_video-replay:hover {
    transform: scale(1.06);
    background: rgba(42, 27, 22, 0.88);
}

.hero_video-replay:focus-visible {
    outline: 3px solid var(--brand-text-light);
    outline-offset: 3px;
}

.hero_video-replay i {
    position: relative;
    z-index: 1;
    font-size: 1rem;
    line-height: 1;
}

.hero_video-progress {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    pointer-events: none;
    transform: rotate(-90deg);
}

.hero_video-progress-track,
.hero_video-progress-value {
    fill: none;
    stroke-width: 2px;
}

.hero_video-progress-track {
    stroke: rgba(255, 255, 255, 0.26);
}

.hero_video-progress-value {
    stroke: rgba(255, 255, 255, 0.96);
    stroke-linecap: round;
    stroke-dasharray: 1;
    stroke-dashoffset: calc(1 - var(--hero-video-progress));
}

.hero-video-control-enter-active,
.hero-video-control-leave-active {
    transition: opacity 0.35s ease, transform 0.35s ease;
}

.hero-video-control-enter-from,
.hero-video-control-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

.hero::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('@/assets/sejarah_thumann_colored.jpg') 50%/cover no-repeat;
    pointer-events: none;

    /* start fully clipped away */
    /* clip-path: circle(0px at var(--x, 50%) var(--y, 50%)); */
    /* transition: clip-path 0.2s ease-out; */
    z-index: 0;

    /* clip-path: circle(var(--r, 0px) at var(--x, 50%) var(--y, 50%)); */
    /* transition: clip-path 0.2s ease-out; */

    /* clip-path: circle(var(--r, 0px) at calc(var(--x) + var(--dx, 0px)) calc(var(--y) + var(--dy, 0px))); */
    /* transition: clip-path 0.2s ease-out; */

    clip-path: circle(var(--r, 0px) at calc(var(--x, 50%) + var(--dx, 0px)) calc(var(--y, 50%) + var(--dy, 0px)));
    transition: clip-path 0.2s ease-out;
}

/* Make sure the navigation, title and stats sit above the isolated media layer. */
.hero>*:not(.hero_media):not(.hero_video-controls) {
    position: relative;
    z-index: 2;
}

.hero :deep(.NavBar) {
    /* Keep the complete navigation stacking context above its fixed dim layer. */
    z-index: 1020 !important;
}

/* End spotlight effect on mouse move */

.hero_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 36px;
    opacity: 1;
    transition: opacity 4s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero_wrapper--video-playing {
    opacity: 0.1;
    transition-duration: 3s;
}

.headline {
    width: calc(100% - 40px);
    max-width: 720px;
}

.subtitle {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    /* align-self: stretch; */
}

.subtitle hr {
    opacity: 0.5;
    background: var(--brand-bg-1);
    width: 72px;
    border: none;
    height: 2px;
}

.subtitle p {
    color: var(--brand-text-light);
    font-family: 'Noto Sans Javanese', 'Inter', sans-serif;
}

.StatBar_container {
    background:
        url('@/assets/noise128x128.png'),
        linear-gradient(to bottom, rgba(0, 0, 0, 0) 50%, var(--brand-bg-2) 50%);
    background-repeat: repeat;
    background-size: auto;
}

.PersonCardGroup_container {
    position: relative;
    overflow-x: hidden;
    overflow-x: clip;
}

/* .button-group {
    display: flex;
    flex-direction: row;
    gap: 16px;
} */

@keyframes slideIn {
    0% {
        transform: translate(-100%, -100%);
    }

    100% {
        transform: translate(100vw, -100%);
    }
}

.decoration_top_left--offset {
    top: 2px;
    left: 20px;
    animation: slideIn 120s linear infinite;
    animation-delay: -30s;
}

.AncestorMap_wrapper {
    display: flex;
    padding: var(--page-padding);
    flex-direction: column;
    align-items: center;
    gap: 32px;
    align-self: stretch;
    position: relative;
}

.AncestorMap_wrapper :deep(.PlaceMap) {
    height: 720px !important;
    width: 100%;
    border-radius: var(--border-radius-lg);
    overflow: hidden;
}

.AncestorMap_wrapper .TitleBtnGroup {
    margin-top: 40px;
}

.DadFamilyTree_container {
    background:
        url('@/assets/noise128x128.png'),
        var(--brand-bg-2);
    background-repeat: repeat;
    background-size: auto;
}

.DadFamilyTree_wrapper {
    display: flex;
    padding: var(--page-padding);
    flex-direction: column;
    align-items: center;
    gap: 32px;
    align-self: stretch;
}

.DadFamilyTree_title_group {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: 8px;
}

.DadFamilyTree_title_group .eyebrow {
    color: var(--brand-text-dark);
}

.DadFamilyTree_title {
    color: var(--brand-text-dark);
}

.DadFamilyTree_title_prefix {
    margin-right: 0.28em;
}

.DadFamilyTree_person_picker {
    position: relative;
    display: inline-flex;
    vertical-align: baseline;
}

.DadFamilyTree_person_trigger {
    appearance: none;
    border: 0;
    background: transparent;
    color: var(--brand-color-1);
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    padding: 0;
    font: inherit;
    line-height: inherit;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: text-decoration-color 0.2s ease, color 0.2s ease;
}

.DadFamilyTree_person_trigger:hover,
.DadFamilyTree_person_trigger:focus-visible {
    color: var(--brand-color-2);
    text-decoration-color: var(--brand-color-1);
    outline: none;
}

.DadFamilyTree_person_caret {
    color: var(--brand-color-1);
    font-size: 0.7em;
    opacity: 0.95;
    transform: translateY(-1px);
}

.DadFamilyTree_person_menu {
    position: absolute;
    top: calc(100% + 12px);
    left: 0;
    right: auto;
    z-index: 20;
    width: min(360px, 86vw);
    padding: 16px 0;
    margin: 0;
    list-style: none;
    border-radius: 16px;
    background-color: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    color: var(--brand-text-dark);
    font-family: var(--font-primary);
    max-height: calc(100vh - 166px - 24px);
    overflow-y: auto;
    display: flex;
    gap: 0;
    flex-direction: column;
    align-items: flex-start;
}

.DadFamilyTree_person_menu li {
    position: relative;
    z-index: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    border-radius: 0;
    color: #1D1D1D;
    padding: 12px 24px;
    font-weight: 600;
    cursor: pointer;
}

.DadFamilyTree_person_menu li:hover,
.DadFamilyTree_person_menu li:focus-visible,
.DadFamilyTree_person_menu_item--active {
    background-color: #f0f0f0;
}

.DadFamilyTree_person_menu li:focus-visible {
    outline: none;
}

.DadFamilyTree_person_menu span {
    color: #1D1D1D;
    line-height: 100%;
}

.DadFamilyTree_person_menu span:nth-of-type(1) {
    font-weight: 600;
    font-size: 16px;
}

.DadFamilyTree_person_menu span:nth-of-type(2) {
    font-size: 12px;
    font-weight: 400;
    opacity: .5;
}

.DadFamilyTree_wrapper :deep(.ParentSection_tree_container) {
    width: 100%;
    box-shadow: var(--shadow-card-lg);
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.century-group {
    display: flex;
    align-items: stretch;
    gap: 24px;
}

.century-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
}

.century-header {
    background: none;
    border: none;
    color: var(--brand-color-3);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 32px;
    padding: 20px 0 16px;
    font-family: var(--font-secondary);
    font-size: 36px;
    font-weight: 600;
    text-align: left;
}

.century-line {
    width: 2px;
    flex: 1;
    margin-right: 16px;
    border-radius: 999px;
    background-color: var(--brand-text-light);
    opacity: 0.2;
}

.century-chevron {
    font-size: 14px;
    opacity: 0.5;
}

.timeline-entries {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.timeline-entry {
    display: flex;
    align-items: center;
}

.no-events-msg {
    margin-top: 32px;
    color: var(--brand-text-light);
    text-align: center;
    opacity: 0.7;
}

.timeline :deep(.PersonCard--small .PersonCard_photo) {
    width: 80px;
    height: 80px;
}

.calendar-cta {
    margin-top: 56px;
}

.calendar-cta :deep(.BtnPrimary) {
    border-color: var(--brand-color-3);
    background: var(--brand-color-3);
    color: var(--brand-text-dark);
    box-shadow: 0 0 0 0 var(--brand-color-3);
}

.calendar-cta :deep(.BtnPrimary:hover) {
    box-shadow: 0 0 0 4px var(--brand-color-3);
}

@keyframes blink {
    50% {
        border-color: transparent;
    }
}

@media (max-width: 860px) {
    .hero {
        height: 84vh !important;
        min-height: auto !important;
    }

    .hero::before {
        display: none;
    }

    .hero_wrapper {
        gap: 24px !important;
    }

    .hero::before {
        clip-path: none !important;
    }

    .AncestorMap_wrapper {
        padding: var(--page-padding-mobile);
    }

    .DadFamilyTree_wrapper {
        padding: var(--page-padding-mobile);
    }

    .DadFamilyTree_person_menu {
        left: auto;
        right: 0;
        transform: none;
    }

    .AncestorMap_wrapper .TitleBtnGroup {
        margin-top: 0px;
    }

    .decoration_top_left--offset {
        height: 48px;
    }

    .decoration_top_right--offset {
        height: 40px;
    }

}

@media (prefers-reduced-motion: reduce) {
    .hero_video {
        display: none;
    }
}

@media (max-width: 720px) {
    .hero_video-controls {
        right: 20px;
        bottom: 76px;
        gap: 10px;
        max-width: calc(100% - 40px);
    }

    .hero_video-identification-person {
        padding: 3px 6px;
        font-size: 0.62rem;
    }

    .century-rail {
        display: none;
    }

    .timeline :deep(.PersonCard--small .PersonCard_photo) {
        width: 48px;
        height: 48px;
    }
}
</style>
